import React, { useState } from 'react';
import { Box, Typography, Stack, Paper, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import ScheduleDropdown from './ScheduleDropdown';

// Mobile-First Styled Components
const ScheduleCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '0.6px solid #E2E8F0',
  padding: '16px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },
}));

const ScheduleContainer = styled(Stack)(({ theme }) => ({
  gap: '16px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#0f172a',
  marginBottom: '4px',
  display: 'block',
  [theme.breakpoints.up('sm')]: {
    fontSize: '15px',
  },
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'block',
    fontSize: '10px',
    fontWeight: 400,
    color: '#EF232A',
    textTransform: 'none',
    letterSpacing: 'normal',
  },
}));

const ScheduleRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '12px',
  alignItems: 'stretch',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'center',
  },
}));

const ScheduleText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: '#64748b',
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#121927',
  },
}));

const DropdownWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    flex: '0 0 auto',
  },
}));

const ScheduleFieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#64748b',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  display: 'block',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

const ScheduleSection = ({ formData, onFormChange, isMobile, isTablet }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const frequencyOptions = ['Weekly', 'Daily', 'Monthly'];
  const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const periodOptions = ['Am', 'Pm'];

  const handleScheduleChange = (field, value) => {
    onFormChange('schedule', {
      ...formData.schedule,
      [field]: value
    });
  };

  // Get responsive dropdown width
  const getDropdownWidth = (type) => {
    if (isSmallScreen) {
      return '100%';
    }
    switch (type) {
      case 'frequency':
        return isMediumScreen ? '130px' : '151px';
      case 'day':
        return isMediumScreen ? '140px' : '157px';
      case 'time':
        return isMediumScreen ? '140px' : '151px';
      default:
        return '151px';
    }
  };

  return (
    <ScheduleCard elevation={0}>
      <ScheduleContainer>
        <SectionTitle>Schedule</SectionTitle>
        <FieldLabel>Schedule*</FieldLabel>

        <ScheduleRow>
          {/* Frequency Dropdown */}
          <DropdownWrapper>
            {isSmallScreen && <ScheduleFieldLabel>Frequency</ScheduleFieldLabel>}
            <ScheduleDropdown
              value={formData.schedule.frequency}
              options={frequencyOptions}
              onChange={(value) => handleScheduleChange('frequency', value)}
              width={getDropdownWidth('frequency')}
              isMobile={isMobile}
            />
          </DropdownWrapper>

          <ScheduleText>on</ScheduleText>

          {/* Day Dropdown */}
          <DropdownWrapper>
            {isSmallScreen && <ScheduleFieldLabel>Day</ScheduleFieldLabel>}
            <ScheduleDropdown
              value={formData.schedule.day}
              options={dayOptions}
              onChange={(value) => handleScheduleChange('day', value)}
              width={getDropdownWidth('day')}
              isMobile={isMobile}
            />
          </DropdownWrapper>

          <ScheduleText>at</ScheduleText>

          {/* Time Dropdown */}
          <DropdownWrapper>
            {isSmallScreen && <ScheduleFieldLabel>Time</ScheduleFieldLabel>}
            <ScheduleDropdown
              value={`${formData.schedule.time} ${formData.schedule.period}`}
              options={[
                '10 : 20 Am', '11 : 30 Am', '12 : 00 Pm',
                '01 : 00 Pm', '02 : 30 Pm', '03 : 45 Pm'
              ]}
              onChange={(value) => {
                const [time, period] = value.split(' ');
                handleScheduleChange('time', time);
                handleScheduleChange('period', period);
              }}
              width={getDropdownWidth('time')}
              isMobile={isMobile}
            />
          </DropdownWrapper>
        </ScheduleRow>
      </ScheduleContainer>
    </ScheduleCard>
  );
};

export default ScheduleSection;
